/**
 * <FileDisplayDetailsDialog> — the workspace file/folder VIEW + EDIT dialog
 * (workspace-file-tags). A centered modal that edits a node's user-facing display
 * attributes. The on-disk machine name NEVER changes (this overlay IS the rename
 * story) and the OS filename is NEVER shown (rule 6).
 *
 * Two branches:
 *   - FILE   → Tag (@handle, C3-validated, case-scope-unique) + Título (citation
 *     name) + Descrição. Saves `document.set_display {tag, title, description}`;
 *     the server's `invalid_tag` / `tag_conflict` / `tag_reclaiming` outcomes render
 *     inline (pure `evaluateTagEdit` pre-checks first).
 *   - FOLDER → Nome (alias) + Descrição (folders are NOT tagged, §12). Saves
 *     `{alias, description}` via the shipped path (pure `evaluateDisplayEdit`).
 *
 * Reached from the tree row-card's pinned pen, the row/folder right-click menu, and
 * the file-pane info bar. On success the tree (+ folder-map) queries are invalidated
 * so every tag/alias surface re-resolves (retroactive rename).
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Eye, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  dialogOverlayCls,
  dialogPanelCls,
  dialogHeaderCls,
  dialogTitleCls,
  dialogCloseBtnCls,
  dialogLabelCls,
  dialogInputCls,
  dialogCancelBtnCls,
  dialogPrimaryBtnCls,
} from "@/components/context-pane/dialog-styles";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { provenanceChipCopy } from "@/components/shell/CaseFileRow";
import { displayGroupKeyForModality } from "@/lib/workspace/case-files-view";
import { useToastStore } from "@/stores/useToastStore";
import { useActiveContextStore, useEffectiveScope } from "@/stores/useActiveContextStore";
import { useWorkspaceAliasMap } from "@/hooks/useWorkspaceAliasMap";
import { normalizeTagKey } from "@/lib/workspace/display-name";
import type { DocumentSetDisplayParams } from "@/types/gateway";
import {
  evaluateDisplayEdit,
  toDisplayPatchFields,
  evaluateTagEdit,
  toTagPatchFields,
} from "./rename-display-edit";


/** Lowercased extension without the dot; "" when the name carries none. */
function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : "";
}

/** Full locale date + time. Unparseable or absent renders NOTHING — a wrong
 *  timestamp is worse than a missing one, and the row is simply omitted. */
function formatDateTime(iso: string | undefined, locale: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Human byte size. `undefined` renders nothing; 0 is a real answer and shows. */
function formatBytes(bytes: number | undefined, locale: string): string {
  if (bytes === undefined) return "";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toLocaleString(locale, { maximumFractionDigits: 1 })} ${units[unit]}`;
}

export interface FileDisplayNode {
  /** Case-root-relative path of the file/folder. */
  path: string;
  isDirectory: boolean;
  /** Raw on-disk name — used internally (node identity); NEVER shown. */
  name: string;
  /** FOLDER: existing alias, if any. */
  alias?: string;
  /** FILE: existing @tag handle (no `@`), if any. */
  tag?: string;
  /** FILE: existing citation title, if any. */
  title?: string;
  /** Existing description, if any. */
  description?: string;

  // ── READ-ONLY facts about the file. Everything below is INFORMATIONAL: the
  //    dialog renders it but never edits it. Only tag / title / description
  //    (and a folder's alias) are the lawyer's to change — the rest is what the
  //    ingest recorded, and letting a form overwrite it would let a typo
  //    rewrite provenance.
  /** Bytes on disk. */
  size?: number;
  /** ISO mtime of the stored entry. */
  modifiedAt?: string;
  /** Server-routed ingest modality — the group this file lives in. */
  modality?: string;
  /** The filename the lawyer actually uploaded. */
  originalName?: string;
  /** Arrival channel of the ORIGINAL (`lexide` renders as "app"). Resolved by
   *  the view model's `sourceId` join, NOT this entry's own writer stamp. */
  channel?: string;
  /** When the ORIGINAL arrived (ISO-8601). */
  uploadedAt?: string;
  /** The ingest's recorded outcome. ABSENT means UNKNOWN, never "fine". */
  ingestStatus?: "ok" | "failed";
  /** Copy/move origin, when the resource was carried in from another scope. */
  provenance?: { from: string; kind: "copied" | "promoted" };
}

interface FileDisplayDetailsDialogProps {
  node: FileDisplayNode;
  /** FOLDER path only: sibling effective names for the D3 alias uniqueness
   *  pre-check (files use the case-scope tag index instead). */
  siblingEffectiveNames: readonly string[];
  onClose: () => void;
  /** Optional "open in content viewer" anchor. When provided AND the node is a
   *  FILE, a left-aligned [Visualizar] button renders in the footer as another
   *  path to the content viewer (operator 2026-07-03). Folders have no viewer,
   *  so it is suppressed there; the file-pane info bar omits it (already open). */
  onVisualize?: () => void;
}

export function FileDisplayDetailsDialog({
  node,
  siblingEffectiveNames,
  onClose,
  onVisualize,
}: FileDisplayDetailsDialogProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "pt-BR";
  const rpc = useGatewayStore((s) => s.rpc);
  const scope = useEffectiveScope();
  const selection = useActiveContextStore((s) => s.selection);
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  const aliasMap = useWorkspaceAliasMap();
  const isFolder = node.isDirectory;

  const [tag, setTag] = useState(node.tag ?? "");
  const [alias, setAlias] = useState(node.alias ?? "");
  // Pre-reseed bridge: a file with a shipped `alias` but no `title` yet pre-fills
  // Título from the alias, so the lawyer edits a populated field (never re-types).
  const [title, setTitle] = useState(node.title ?? node.alias ?? "");
  const [description, setDescription] = useState(node.description ?? "");
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTag(node.tag ?? "");
    setAlias(node.alias ?? "");
    setTitle(node.title ?? node.alias ?? "");
    setDescription(node.description ?? "");
    setSaving(false);
    setServerError(null);
  }, [node.path, node.tag, node.alias, node.title, node.description]);

  useEffect(() => {
    firstFieldRef.current?.select();
  }, []);

  // Case-scope tag keys EXCLUDING this file (the uniqueness pre-check set).
  const otherTagKeys = useMemo(() => {
    const selfKey = node.tag?.trim() ? normalizeTagKey(node.tag) : "";
    const s = new Set<string>();
    for (const k of aliasMap.byTag.keys()) if (k !== selfKey) s.add(k);
    return s;
  }, [aliasMap.byTag, node.tag]);

  const folderState = useMemo(
    () =>
      evaluateDisplayEdit({
        alias,
        description,
        currentAlias: node.alias,
        currentDescription: node.description,
        siblingEffectiveNames,
      }),
    [alias, description, node.alias, node.description, siblingEffectiveNames],
  );
  const fileState = useMemo(
    () =>
      evaluateTagEdit({
        tag,
        title,
        description,
        currentTag: node.tag,
        // Match the Título pre-fill (node.title ?? node.alias) so a pristine open
        // of a pre-reseed file (alias set, title unset) is NOT dirty.
        currentTitle: node.title ?? node.alias,
        currentDescription: node.description,
        otherTagKeys,
      }),
    [tag, title, description, node.tag, node.title, node.alias, node.description, otherTagKeys],
  );
  const canSave = isFolder ? folderState.canSave : fileState.canSave;

  const err = (code: string | undefined): string | null =>
    code ? t(`displayRename.errors.${code}`) : null;
  const aliasErrorMsg = folderState.conflictWith
    ? t("displayRename.errors.conflict", { name: folderState.conflictWith })
    : err(folderState.aliasError);
  const tagErrorMsg = fileState.tagConflict
    ? t("displayRename.errors.tagConflict")
    : fileState.tagError
      ? t(`displayRename.errors.tag.${fileState.tagError}`)
      : null;
  const titleErrorMsg = err(fileState.titleError);
  const descriptionErrorMsg = isFolder ? err(folderState.descriptionError) : err(fileState.descriptionError);


  /**
   * The read-only facts, in the order a lawyer would ask them: what did I send,
   * where did it come from, when, what kind of thing is it, and did it work.
   * Only rows whose datum exists are produced.
   */
  const infoRows = useMemo(() => {
    const rows: { key: string; value: string; mono?: boolean; warn?: boolean }[] = [];
    const push = (key: string, value: string | undefined | null, extra: { mono?: boolean; warn?: boolean } = {}) => {
      if (value) rows.push({ key, value, ...extra });
    };
    push("originalName", node.originalName, { mono: true });
    push(
      "origin",
      node.channel
        ? // OPEN vocabulary — an unrecognised channel renders VERBATIM rather
          // than through a missing key.
          (t(`caseFiles.channels.${node.channel}`, { defaultValue: node.channel }) as string)
        : undefined,
    );
    // A full DATETIME here, not the tree's abbreviated chip: this is the
    // reference surface, so the precise instant is the useful form.
    push("uploadedAt", formatDateTime(node.uploadedAt, locale));
    const ext = extensionOf(node.originalName ?? node.name);
    push("format", ext ? `.${ext}` : "", { mono: true });
    push("size", formatBytes(node.size, locale));
    // The GROUP's label, never the raw server token: `office` and `doc` are
    // routing vocabulary, and the lawyer reads "Planilhas e apresentações".
    // An unknown modality still renders verbatim rather than vanishing.
    {
      const groupKey = displayGroupKeyForModality(node.modality);
      push(
        "modality",
        groupKey ? (t(`caseFiles.groups.${groupKey}`) as string) : node.modality,
      );
    }
    push("modifiedAt", formatDateTime(node.modifiedAt, locale));
    if (node.provenance) {
      const copy = provenanceChipCopy(node.provenance, t);
      push("provenance", copy.short);
    }
    // Only the FAILED state is surfaced. "ok" needs no line, and ABSENT means
    // the outcome is unknown — which must not be rendered as success.
    if (node.ingestStatus === "failed") {
      push("ingestStatus", t("caseFiles.failedBadge"), { warn: true });
    }
    return rows;
  }, [node, t, locale]);

  const handleSave = useCallback(async () => {
    if (!rpc || !canSave || saving) return;
    const patch = isFolder
      ? { ...toDisplayPatchFields(alias, description) }
      : toTagPatchFields(tag, title, description);
    const base = { path: node.path, isDirectory: node.isDirectory, ...patch };
    let params: DocumentSetDisplayParams;
    if (scope === "org") {
      params = { scope: "org", ...base };
    } else {
      if (!selection?.clientId || !selection?.matterId || !selection?.caseId) {
        setServerError(t("displayRename.errors.generic"));
        return;
      }
      params = {
        clientId: selection.clientId,
        matterId: selection.matterId,
        caseId: selection.caseId,
        ...base,
      };
    }
    setSaving(true);
    setServerError(null);
    try {
      const res = await rpc.documentSetDisplay(params);
      if (res.ok) {
        void queryClient.invalidateQueries({ queryKey: ["case-workspace-tree"] });
        if (node.isDirectory) {
          void queryClient.invalidateQueries({ queryKey: ["case-folder-map"] });
        }
        // Success cue — the dialog closes and the tree re-resolves, but a brief
        // toast confirms the save landed (parity with copy/move; the operator
        // wants an explicit success signal, not just a silent close).
        addToast({ text: t("displayRename.saved"), kind: "success", autoDismissMs: 3000 });
        onClose();
        return;
      }
      switch (res.error) {
        case "display_name_conflict":
          setServerError(t("displayRename.errors.conflict", { name: res.conflict.name }));
          break;
        case "invalid_display_name":
          setServerError(t(`displayRename.errors.${res.reason}`));
          break;
        case "invalid_tag":
          setServerError(t(`displayRename.errors.tag.${res.reason}`));
          break;
        case "tag_conflict":
          setServerError(t("displayRename.errors.tagConflictNamed", { title: res.holder.title }));
          break;
        case "tag_reclaiming":
          setServerError(t("displayRename.errors.tagReclaiming"));
          break;
        default:
          setServerError(t("displayRename.errors.forbidden"));
      }
    } catch {
      setServerError(t("displayRename.errors.generic"));
    } finally {
      setSaving(false);
    }
  }, [rpc, canSave, saving, isFolder, alias, tag, title, description, scope, selection, node, queryClient, onClose, addToast, t]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const modalTitle = isFolder ? t("displayRename.titleFolder") : t("displayRename.title");
  const onEnterSave = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void handleSave();
    }
  };
  const inputCls = (hasError: boolean) =>
    cn(dialogInputCls, hasError && "border-red-400 focus:border-red-400 focus:ring-red-400/40");

  return createPortal(
    // Modal — clicking the backdrop does NOT close it (operator 2026-07-03); a
    // misclick must not discard in-progress edits. Close via Cancel / X / Esc.
    <div data-testid="file-display-details-dialog" className={dialogOverlayCls}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={modalTitle}
        onClick={(e) => e.stopPropagation()}
        className={dialogPanelCls}
      >
        <div className={dialogHeaderCls}>
          <h2 className={dialogTitleCls}>{modalTitle}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("actions.close")}
            className={dialogCloseBtnCls}
          >
            <X size={16} />
          </button>
        </div>

        {isFolder ? (
          <>
            {/* FOLDER — Nome (alias) */}
            <label className={dialogLabelCls}>{t("displayRename.nameLabel")}</label>
            <input
              ref={firstFieldRef}
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              onKeyDown={onEnterSave}
              placeholder={t("displayRename.namePlaceholder")}
              maxLength={120}
              className={inputCls(!!aliasErrorMsg)}
            />
            {aliasErrorMsg && <p className="mt-0.5 text-[11px] text-red-500">{aliasErrorMsg}</p>}
          </>
        ) : (
          <>
            {/* FILE — Tag (the handle; primary in-house reference). No `@` shown
                (operator 2026-07-03) — the mono field + label are the cue. */}
            <label className={dialogLabelCls}>{t("displayRename.tagLabel")}</label>
            <input
              ref={firstFieldRef}
              data-testid="file-display-tag-input"
              value={tag.replace(/^@/, "")}
              onChange={(e) => setTag(e.target.value.replace(/^@/, ""))}
              onKeyDown={onEnterSave}
              placeholder={t("displayRename.tagPlaceholder")}
              maxLength={40}
              className={cn(inputCls(!!tagErrorMsg), "font-mono font-medium")}
            />
            {tagErrorMsg && <p className="mt-0.5 text-[11px] text-red-500">{tagErrorMsg}</p>}

            {/* FILE — Título (citation name) */}
            <label className={cn(dialogLabelCls, "mt-4")}>{t("displayRename.titleLabel")}</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={onEnterSave}
              placeholder={t("displayRename.titlePlaceholder")}
              maxLength={120}
              className={inputCls(!!titleErrorMsg)}
            />
            {titleErrorMsg && <p className="mt-0.5 text-[11px] text-red-500">{titleErrorMsg}</p>}
          </>
        )}

        {/* Description (both) */}
        <label className={cn(dialogLabelCls, "mt-4")}>{t("displayRename.descriptionLabel")}</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t("displayRename.descriptionPlaceholder")}
          maxLength={500}
          rows={4}
          className={cn(
            dialogInputCls,
            "resize-y leading-relaxed",
            descriptionErrorMsg && "border-red-400 focus:border-red-400 focus:ring-red-400/40",
          )}
        />
        {descriptionErrorMsg && <p className="mt-0.5 text-[11px] text-red-500">{descriptionErrorMsg}</p>}

        {serverError && <p className="mt-2 text-[11px] text-red-500">{serverError}</p>}

        {/* ─── INFORMATION (read-only) ─────────────────────────────────────
            What the ingest recorded about the ORIGINAL. Rendered here because
            this dialog is where a lawyer comes to ask "what IS this file?",
            and the answer was previously split between an expanded tree row
            and nowhere at all.

            Strictly informational: none of it is a form field. These are facts
            the pipeline wrote, and a text input over them would let a typo
            rewrite provenance. Each line renders only when its datum exists —
            absence is silence, never a placeholder or an em dash standing in
            for "unknown". */}
        {!isFolder && infoRows.length > 0 && (
          <div
            data-testid="file-display-details-info"
            className="mt-5 border-t border-cream-200/70 pt-4 dark:border-coffee-700/50"
          >
            <p className={dialogLabelCls}>{t("displayRename.infoHeading")}</p>
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-[12px]">
              {infoRows.map((row) => (
                <Fragment key={row.key}>
                  <dt className="whitespace-nowrap text-cream-500 dark:text-cream-500">
                    {t(`displayRename.info.${row.key}`)}
                  </dt>
                  <dd
                    data-testid={`file-display-info-${row.key}`}
                    className={cn(
                      "min-w-0 break-words text-coffee-700 dark:text-cream-300",
                      row.mono && "font-mono text-[11px]",
                      row.warn && "font-medium text-red-600 dark:text-red-400",
                    )}
                  >
                    {row.value}
                  </dd>
                </Fragment>
              ))}
            </dl>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between gap-2">
          {/* Left anchor: open the file in the content viewer. FILE-only (folders
              have no viewer); the empty <div> keeps Cancelar/Salvar right-aligned
              when it is absent. */}
          <div>
            {!isFolder && onVisualize && (
              <button
                type="button"
                data-testid="file-display-visualize"
                onClick={onVisualize}
                className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-body-sm text-coffee-600 hover:bg-cream-100/70 dark:text-cream-300 dark:hover:bg-coffee-800/60"
              >
                <Eye size={13} />
                {t("displayRename.visualizeAction")}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className={dialogCancelBtnCls}>
              {t("actions.cancel")}
            </button>
            <button
              type="button"
              data-testid="file-display-save"
              onClick={() => void handleSave()}
              disabled={!canSave || saving}
              className={dialogPrimaryBtnCls(canSave && !saving)}
            >
              {saving && <Loader2 size={12} className="animate-spin" />}
              {t("displayRename.rename")}
            </button>
          </div>
        </div>
      </div>
    </div>,
    // Portalled to <body> so the modal can never be trapped by an ancestor's
    // containing block. A `position: fixed` overlay is only viewport-relative
    // while no ancestor establishes a containing block — any `transform`,
    // `filter`, or containment (`content-visibility`) silently re-parents it,
    // which is exactly how this dialog ended up rendering INSIDE the side pane.
    // Portalling makes the modal independent of whatever the tree does above it.
    document.body,
  );
}
